import React from 'react';
import { motion } from 'framer-motion';
import { SlotMachineButton } from './SlotMachineButton';
import { ArrowRight, TickIcon } from './icons';
import { useExpectationsSection } from '../hooks/useExpectationsSection';

const ExpectationsSection: React.FC = () => {
  const { data, loading, error } = useExpectationsSection();
  
  // Debug logging for expectations section data
  if (data && !loading && !error) {
    console.log('📋 ExpectationsSection - ✅ Strapi data loaded:', !!data);
    console.log('📋 ExpectationsSection - Data keys:', Object.keys(data || {}));
    console.log('📋 ExpectationsSection - Title:', data.title);
    console.log('📋 ExpectationsSection - Expectations count:', data.expectations?.length || 0);
    console.log('📋 ExpectationsSection - Button:', data.button);
  }

  // Fallback data
  const defaultData = {
    title: "Wat kun je verwachten?",
    expectations: [
      {
        title: "Zichtbaar resultaat in weken",
        description: "De meeste cliënten zien minder haaruitval na 2-4 weken en nieuwe haargroei binnen 4-8 weken, afhankelijk van het behandelplan."
      },
      {
        title: "Weinig ongemak, direct je dag vervolgen",
        description: "De prikjes voelen als lichte tikjes en laten meestal alleen wat roodheid achter die binnen enkele uren wegtrekt. Geen littekens, geen lange hersteltijd."
      },
      {
        title: "Duidelijke kosten vooraf",
        description: "Tijdens je intake weet je direct hoeveel sessies je nodig hebt en welk pakket of losse behandeling daarbij past. Zo kom je nooit voor verrassingen te staan."
      }
    ],
    description: "Tijdens je intake bespreken we hoeveel sessies je nodig hebt en welk pakket of losse behandeling daarbij past.",
    button: {
      text: "Bekijk de tarieven",
      url: "/tarieven",
      variant: "tertiary" as const,
      isExternal: false
    }
  };

  const sectionData = data || defaultData;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  if (loading) {
    return (
      <section className="expectations-section">
        <div className="container">
          <div className="expectations-content">
            <div className="loading-placeholder">
              <div className="loading-title"></div>
            </div>
            <div className="expectations-grid">
              {[1, 2, 3].map((i) => (
                <div key={i} className="expectation-card">
                  <div className="loading-placeholder">
                    <div className="loading-title"></div>
                    <div className="loading-text"></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="expectations-footer">
              <div className="loading-placeholder">
                <div className="loading-text"></div>
              </div>
              <div className="loading-placeholder">
                <div className="loading-button"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="expectations-section">
        <div className="container">
          <div className="expectations-content">
            <h2 className="expectations-title">{defaultData.title}</h2>
            <div className="expectations-grid">
              {defaultData.expectations.map((expectation, index) => (
                <div key={expectation.title || index} className="expectation-card">
                  <div className="check-icon-container">
                    <TickIcon size={16} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">{expectation.title}</h3>
                    <p className="card-description">{expectation.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="expectations-footer">
              <p className="expectations-description">{defaultData.description}</p>
              <SlotMachineButton
                text={defaultData.button.text}
                icon={<ArrowRight size={20} />}
                href={defaultData.button.url}
                className="expectations-button"
                variant="tertiary"
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="expectations-section">
      <div className="container">
        <motion.div 
          className="expectations-content"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 
            className="expectations-title"
            variants={itemVariants}
          >
            {sectionData.title}
          </motion.h2>
          
          <motion.div 
            className="expectations-grid"
            variants={itemVariants}
          >
            {sectionData.expectations?.map((expectation) => (
              <motion.div 
                key={expectation.id || expectation.title} 
                className="expectation-card"
                variants={itemVariants}
              >
                <div className="check-icon-container">
                  <TickIcon size={16} />
                </div>
                <div className="card-content">
                  <h3 className="card-title">{expectation.title}</h3>
                  <p className="card-description">{expectation.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div 
            className="expectations-footer"
            variants={itemVariants}
          >
            <p className="expectations-description">{sectionData.description}</p>
            <SlotMachineButton
              text={sectionData.button.text}
              icon={<ArrowRight size={20} />}
              href={sectionData.button.url}
              className="expectations-button"
              variant={sectionData.button.variant}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExpectationsSection; 